import usePageMeta from '../hooks/usePageMeta';
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { categories as mockCategories, products as mockProducts, spotlight as mockSpotlight, LOGO_URL } from '../mock';
import { fetchProducts, fetchCategories, fetchHappyClients, fetchSpotlight } from '../api';
import ClientReviewsWall from '../components/ClientReviewsWall';
import ClientStoryReel from '../components/ClientStoryReel';
import ProductCard from '../components/ProductCard';
import { opt, srcset } from '../lib/media';
import InstagramFeed from '../components/InstagramFeed';
import CustomOrderStrip from '../components/CustomOrderStrip';

function Hero({ products, categories, loaded }) {
  const [i, setI] = useState(0);
  const videoRef = useRef(null);

  const catName = (slug) => (categories.find((c) => c.slug === slug)?.name) || 'HITÉRA';

  // Build hero slides from real products — prefer reels, fall back to photos
  const withVideo = products.filter((p) => p.videos && p.videos.length);
  const source = withVideo.length ? withVideo.slice(-6).reverse() : products.slice(0, 6);
  const productSlides = source.map((p) => ({
    id: p.id,
    type: (p.videos && p.videos.length) ? 'video' : 'image',
    src: (p.videos && p.videos.length) ? p.videos[0] : (p.images && p.images[0]),
    poster: p.images && p.images[0],
    eyebrow: catName(p.category),
    title: p.name,
    subtitle: 'A HITÉRA signature, crafted to be treasured.',
    cta: 'Shop This Look',
    link: `/product/${p.id}`,
  }));

  const slides = productSlides;
  const safeIndex = slides.length ? i % slides.length : 0;
  const s = slides[safeIndex];

  useEffect(() => { setI(0); }, [slides.length]);

  // Ensure video is muted for autoplay (React manages muted via DOM property, not attribute)
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.defaultMuted = true;
      videoRef.current.muted = true;
    }
  }, [safeIndex]);

  // Advance slides: videos advance on end (with safety timer); images on interval
  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const isVideo = slides[safeIndex]?.type === 'video';
    const delay = isVideo ? 16000 : 6500;
    const t = setTimeout(() => setI((p) => (p + 1) % slides.length), delay);
    return () => clearTimeout(t);
  }, [safeIndex, slides.length]); // eslint-disable-line

  const next = () => setI((p) => (p + 1) % slides.length);

  // Branded loader — never flash old/stale imagery before real data arrives
  if (!loaded || slides.length === 0) {
    return (
      <section className="relative h-[78vh] md:h-[92vh] overflow-hidden bg-burgundy-950 flex flex-col items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-t from-burgundy-950 via-burgundy-900/40 to-burgundy-950" />
        <div className="relative flex flex-col items-center">
          <img src={opt(LOGO_URL, 240)} alt="HITÉRA" className="h-24 md:h-28 rounded-sm opacity-90 animate-pulse" />
          <div className="mt-8 h-[2px] w-24 bg-gold/30 overflow-hidden rounded-full">
            <div className="h-full w-1/2 bg-gold animate-[shimmer_1.4s_ease-in-out_infinite]" />
          </div>
        </div>
      </section>
    );
  }

  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;

  return (
    <section className="relative hero-fullscreen overflow-hidden bg-burgundy-950">
      {slides.map((slide, idx) => (
        <div key={`${slide.id}-${idx}`} className={`absolute inset-0 transition-opacity duration-[1200ms] ${idx === safeIndex ? 'opacity-100' : 'opacity-0'}`}>
          {slide.type === 'video' && !isMobile ? (
            idx === safeIndex ? (
              <video
                ref={videoRef}
                src={slide.src}
                poster={opt(slide.poster, 1200)}
                className="w-full h-full object-cover"
                autoPlay={true}
                muted={true}
                playsInline={true}
                loop={true}
                preload="metadata"
                onEnded={next}
              />
            ) : (
              <img src={opt(slide.poster, 1600)} alt={slide.title} loading="lazy" decoding="async" width="1200" height="1600" className="w-full h-full object-cover" />
            )
          ) : (
            <img src={opt(slide.type === 'video' ? slide.poster : slide.src, 1600)} alt={slide.title} loading={idx === 0 ? 'eager' : 'lazy'} fetchPriority={idx === 0 ? 'high' : 'auto'} decoding="async" width="1200" height="1600" className={`w-full h-full object-cover ${idx === safeIndex ? 'kenburns' : ''}`} />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-burgundy-950/85 via-burgundy-950/25 to-burgundy-950/40" />
        </div>
      ))}

      <div className="relative h-full flex flex-col items-center justify-end pb-24 md:pb-28 text-center px-6">
        <p key={`e${safeIndex}`} className="text-gold tracking-luxe text-xs md:text-sm uppercase mb-4 fade-up">{s.eyebrow}</p>
        <h1 key={`t${safeIndex}`} className="font-display text-5xl md:text-7xl text-cream mb-4 fade-up max-w-4xl leading-none">{s.title}</h1>
        <p key={`s${safeIndex}`} className="text-cream/80 max-w-xl mb-8 fade-up text-sm md:text-base">{s.subtitle}</p>
        <Link key={`c${safeIndex}`} to={s.link} className="fade-up group inline-flex items-center gap-3 bg-cream text-burgundy-950 px-9 py-3.5 text-xs tracking-wideluxe uppercase hover:bg-gold transition-colors duration-300">
          {s.cta} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2.5">
        {slides.map((_, idx) => (
          <button key={idx} onClick={() => setI(idx)} className={`h-[2px] transition-all duration-500 ${idx === safeIndex ? 'w-10 bg-gold' : 'w-5 bg-cream/40'}`} aria-label={`Slide ${idx + 1}`} />
        ))}
      </div>
    </section>
  );
}

function SectionTitle({ eyebrow, title, sub }) {
  return (
    <div className="text-center mb-12">
      {eyebrow && <p className="text-gold-dark tracking-luxe text-xs uppercase mb-3">{eyebrow}</p>}
      <h2 className="font-display text-4xl md:text-5xl text-burgundy-900">{title}</h2>
      <div className="gold-divider" />
      {sub && <p className="text-burgundy-900/60 max-w-xl mx-auto text-sm">{sub}</p>}
    </div>
  );
}

function ShopByCategory({ cats, products, loaded }) {
  const tileImage = (c) => {
    const prod = products.find((p) => p.category === c.slug && p.images && p.images[0]);
    return (prod && prod.images[0]) || c.image;
  };
  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <SectionTitle eyebrow="Explore" title="Shop By Category" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {!loaded
            ? Array.from({ length: 4 }).map((_, idx) => (
                <div key={idx} className="aspect-[3/4] skeleton" />
              ))
            : cats.map((c, idx) => (
            <Link key={c.slug} to={`/shop/${c.slug}`} className="group fade-up" style={{ animationDelay: `${idx * 0.06}s` }}>
              <div className="img-zoom relative aspect-[3/4] overflow-hidden">
                <img src={opt(tileImage(c), 800)} srcSet={srcset(tileImage(c))} sizes="(min-width: 1024px) 25vw, 50vw" alt={c.name} loading="lazy" decoding="async" width="600" height="800" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-burgundy-950/70 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 text-center">
                  <h3 className="font-display text-xl md:text-2xl text-cream">{c.name}</h3>
                  <p className="text-gold text-[10px] tracking-wideluxe uppercase mt-1 opacity-0 group-hover:opacity-100 transition-opacity">{c.tagline}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function DiscoverBanner() {
  const img = "https://customer-assets-jt897jd0.emergentagent.net/job_hitera-boutique/artifacts/6q0nvrli_20260728_123147_IMG_1576.webp";
  return (
    <section className="grid md:grid-cols-2">
      <div className="relative h-[70vh] md:h-[90vh] overflow-hidden bg-burgundy-950">
        {/* blurred fill so the full photo shows without cropping */}
        <img src={opt(img, 64)} alt="" aria-hidden="true" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-50" />
        <img src={opt(img, 1000)} srcSet={srcset(img, [600, 1000])} sizes="(min-width: 768px) 50vw, 100vw" alt="Discover" loading="lazy" decoding="async" width="900" height="1200" className="relative w-full h-full object-contain" />
      </div>
      <div className="bg-burgundy-900 flex items-center justify-center px-8 py-20 md:py-0">
        <div className="max-w-md text-center">
          <p className="text-gold tracking-luxe text-xs uppercase mb-4">The Signature Line</p>
          <h2 className="font-display text-4xl md:text-5xl text-cream mb-5 leading-tight">Effortless By Design</h2>
          <p className="text-cream/70 mb-8 text-sm leading-relaxed">Fluid drapes and relaxed silhouettes, hand-crafted to move with you. Every HITÉRA creation is made to be lived in — and loved.</p>
          <Link to="/shop" className="inline-flex items-center gap-3 border border-gold text-gold px-9 py-3.5 text-xs tracking-wideluxe uppercase hover:bg-gold hover:text-burgundy-950 transition-colors duration-300">
            Discover Now <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function ProductRail({ title, eyebrow, items, link, loaded }) {
  const ref = useRef(null);
  const scroll = (dir) => {
    if (ref.current) ref.current.scrollBy({ left: dir * 340, behavior: 'smooth' });
  };
  return (
    <section className="py-20 md:py-28 bg-cream/40">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-gold-dark tracking-luxe text-xs uppercase mb-2">{eyebrow}</p>
            <h2 className="font-display text-4xl md:text-5xl text-burgundy-900">{title}</h2>
          </div>
          <div className="hidden md:flex gap-2">
            <button onClick={() => scroll(-1)} className="w-11 h-11 border border-burgundy-900/20 flex items-center justify-center hover:bg-burgundy-900 hover:text-cream transition-colors"><ChevronLeft size={18} /></button>
            <button onClick={() => scroll(1)} className="w-11 h-11 border border-burgundy-900/20 flex items-center justify-center hover:bg-burgundy-900 hover:text-cream transition-colors"><ChevronRight size={18} /></button>
          </div>
        </div>
        <div ref={ref} className="flex gap-4 md:gap-6 overflow-x-auto pb-4 snap-x" style={{ scrollbarWidth: 'none' }}>
          {!loaded
            ? Array.from({ length: 4 }).map((_, idx) => (
                <div key={idx} className="min-w-[75%] sm:min-w-[45%] md:min-w-[300px] snap-start">
                  <div className="aspect-[3/4] skeleton" />
                  <div className="h-4 skeleton mt-4 w-2/3 mx-auto" />
                  <div className="h-3 skeleton mt-2 w-1/3 mx-auto" />
                </div>
              ))
            : items.map((p, idx) => (
            <div key={p.id} className="min-w-[75%] sm:min-w-[45%] md:min-w-[300px] snap-start">
              <ProductCard product={p} index={idx} />
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to={link} className="inline-flex items-center gap-3 border-b border-gold text-burgundy-900 pb-1 text-xs tracking-wideluxe uppercase hover:text-gold-dark transition-colors">
            Shop The Collection <ArrowRight size={15} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function Spotlight({ items }) {
  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <SectionTitle eyebrow="Curated" title="In The Spotlight" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {items.map((s, idx) => (
            <Link key={s.id || idx} to={s.link || '/shop'} className="group fade-up" style={{ animationDelay: `${idx * 0.06}s` }}>
              <div className="img-zoom relative aspect-[3/4] overflow-hidden">
                <img src={opt(s.image, 700)} srcSet={srcset(s.image, [400, 700])} sizes="(min-width: 768px) 33vw, 50vw" alt={s.label} loading="lazy" decoding="async" width="525" height="700" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-burgundy-950/25 group-hover:bg-burgundy-950/40 transition-colors" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <h3 className="font-display text-2xl md:text-3xl text-cream text-center px-4">{s.label}</h3>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function EditorialBanner() {
  const img = "https://customer-assets-jt897jd0.emergentagent.net/job_hitera-boutique/artifacts/lnuxd9ye_20260825_172613_IMG_4455.webp";
  return (
    <section className="relative h-[80vh] md:h-[92vh] overflow-hidden bg-burgundy-950">
      {/* blurred fill so the full photo shows without cropping */}
      <img src={opt(img, 64)} alt="" aria-hidden="true" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-60" />
      <img src={opt(img, 1200)} srcSet={srcset(img, [700, 1200])} sizes="(min-width: 768px) 60vw, 100vw" alt="HITÉRA Muse" loading="lazy" decoding="async" width="1125" height="2000" className="relative w-full h-full object-contain" />
      <div className="absolute inset-0 bg-burgundy-950/35" />
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
        <p className="text-gold tracking-luxe text-xs uppercase mb-4">The Muse | FW'25</p>
        <h2 className="font-display text-5xl md:text-6xl text-cream mb-5 max-w-3xl leading-tight">Where Craft Becomes Couture</h2>
        <p className="text-cream/80 max-w-lg mb-8 text-sm">A love for artistry that endures — honouring the hands that create and the woman who wears it.</p>
        <Link to="/about" className="inline-flex items-center gap-3 bg-cream text-burgundy-950 px-9 py-3.5 text-xs tracking-wideluxe uppercase hover:bg-gold transition-colors">Discover The Story</Link>
      </div>
    </section>
  );
}

function HappyClientsTeaser({ clients }) {
  return (
    <section className="py-20 md:py-28 bg-cream/40">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <SectionTitle eyebrow="Real Women, Real Moments" title="Happy Clients" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {clients.slice(0, 4).map((c, idx) => (
            <div key={c.id} className="fade-up bg-white overflow-hidden" style={{ animationDelay: `${idx * 0.06}s` }}>
              <div className="img-zoom aspect-[4/5] overflow-hidden bg-cream">
                {c.image && <img src={opt(c.image, 600)} alt={c.name} loading="lazy" decoding="async" width="480" height="600" className="w-full h-full object-cover" />}
              </div>
              <div className="p-4 text-center">
                <p className="text-burgundy-900/70 text-xs italic leading-relaxed line-clamp-3">“{c.testimonial}”</p>
                <p className="font-display text-lg text-burgundy-900 mt-2">{c.name}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/happy-clients" className="inline-flex items-center gap-3 border-b border-gold text-burgundy-900 pb-1 text-xs tracking-wideluxe uppercase hover:text-gold-dark transition-colors">View All Happy Clients <ArrowRight size={15} /></Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [products, setProducts] = useState([]);
  usePageMeta('Hitera | Luxury Designer Wear – Indo-Western, Wedding & Traditional Couture', 'Hitera (Hitéra) is a luxury designer wear brand from Mumbai, India. Shop Indo-western, western, wedding and traditional designer wear — gowns, designer dresses and couture.');
  const [happyClients, setHappyClients] = useState([]);
  const [spotlight, setSpotlight] = useState(mockSpotlight);
  const [categories, setCategories] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([fetchProducts(), fetchCategories()])
      .then(([p, c]) => {
        setProducts(Array.isArray(p) ? p : []);
        setCategories(Array.isArray(c) && c.length ? c : mockCategories);
      })
      .catch(() => { setProducts(mockProducts); setCategories(mockCategories); })
      .finally(() => setLoaded(true));
    fetchSpotlight().then((d) => { if (Array.isArray(d) && d.length) setSpotlight(d); }).catch(() => {});
    fetchHappyClients().then((d) => { if (Array.isArray(d)) setHappyClients(d); }).catch(() => {});
  }, []);

  const newArrivals = products.filter((p) => p.isNew).concat(products).slice(0, 8);
  const gowns = products.filter((p) => p.category === 'gowns');
  return (
    <div>
      <Hero products={products} categories={categories} loaded={loaded} />
      <Link to="/diwali" data-testid="diwali-banner" className="block bg-burgundy-900 text-center py-4 px-6 hover:bg-burgundy-800 transition-colors">
        <span className="text-gold tracking-wideluxe text-[11px] uppercase">✦ The Diwali Edit ✦</span>
        <span className="text-cream text-sm mx-3 hidden sm:inline">Custom-made in 2–3 weeks — delivered before Diwali</span>
        <span className="text-gold text-[11px] tracking-wideluxe uppercase underline underline-offset-4 whitespace-nowrap">Reserve Your Slot</span>
      </Link>
      <ShopByCategory cats={categories} products={products} loaded={loaded} />
      <EditorialBanner />
      <ProductRail eyebrow="Just In" title="New Arrivals" items={newArrivals} link="/shop" loaded={loaded} />
      <Spotlight items={spotlight} />
      <DiscoverBanner />
      <ProductRail eyebrow="Signature" title="The Gown Edit" items={gowns} link="/shop/gowns" loaded={loaded} />
      {happyClients.length > 0 && <HappyClientsTeaser clients={happyClients} />}
      <ClientStoryReel />
      <ClientReviewsWall limit={6} />
      <CustomOrderStrip />
      <InstagramFeed />
    </div>
  );
}
